import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import {
  JURISDICTION_VIEW,
  ALERTS_NOTIFICATION,
} from '@rsos/capstone/src/constants/capabilities';
import { ALERTS } from '@rsos/capstone/src/constants/permissionNames';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import { useFlag } from '@rsos/flags';
import useIsInteragencyChatEnabled from '@rsos/hooks/useIsInteragencyChatEnabled';
import { selectHasAgent511Access } from '@rsos/reduxDataStore/src/features/audioNotifications/smsHelpers';
import {
  selectOrgAudioSettings,
  selectUserAudioSettings,
} from '@rsos/sinatra/src/selectors/selectAudioSettings.ts';
import SettingsNonResponsive from './SettingsNonResponsive';
import SettingsResponsive from './SettingsResponsive';
import { getFilteredAudioSettings } from './helpers';

const Settings = ({ path, themeName }) => {
  const { layout } = useContext(LayoutContext);
  const hasAgent511Access = useSelector(selectHasAgent511Access);

  const isJVEnabled = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP &&
        state.psaps.currentPSAP.activeCapabilities &&
        state.psaps.currentPSAP.activeCapabilities[JURISDICTION_VIEW]
      ),
  );

  const orgAudioSettings = useSelector(selectOrgAudioSettings('capstone'));

  const userAudioSettings = useSelector(selectUserAudioSettings);

  const isDisplayAlerts = useSelector(state => {
    return (
      state.sinatra.user?.currentRole?.permissions?.includes(ALERTS) ||
      state.psaps.currentPSAP?.activeCapabilities?.[ALERTS_NOTIFICATION]
    );
  });

  // Same gate as the playback hook: capability/permission + flag.
  const hasInteragencyChat = useIsInteragencyChatEnabled();
  const isInteragencyChatFlagEnabled = useFlag(
    'interagency_chat_notifications',
  );
  const isInteragencyChatEnabled =
    hasInteragencyChat && isInteragencyChatFlagEnabled;

  const filteredOrgAudioSettings = getFilteredAudioSettings(
    orgAudioSettings,
    isJVEnabled,
    isDisplayAlerts,
    hasAgent511Access,
    isInteragencyChatEnabled,
  );

  let filteredUserAudioSettings = getFilteredAudioSettings(
    userAudioSettings,
    isJVEnabled,
    isDisplayAlerts,
    hasAgent511Access,
    isInteragencyChatEnabled,
  );

  return layout.window.width <= BP_LAPTOP ? (
    <SettingsResponsive
      path={path}
      userAudioSettings={filteredUserAudioSettings}
      orgAudioSettings={filteredOrgAudioSettings}
    />
  ) : (
    <SettingsNonResponsive
      data-name="navigation-audio-control"
      path={path}
      themeName={themeName}
      userAudioSettings={filteredUserAudioSettings}
      orgAudioSettings={filteredOrgAudioSettings}
    />
  );
};

export default Settings;
